<script lang="ts">
import { useProspect } from "~/composables/useProspect";
import { useCore } from "~/composables/useCore";
import { ProspectPageEnum } from "~~/enums/ProspectPage";
import CurrentPage from "~~/models/CurrentPage";
import { LocalStorageConst } from "~~/consts/LocalStorage";

definePageMeta({
  layout: "default",
});

export default {
  data() {
    return {
      pageIsLoading: true,
      prospectIsLoading: false as unknown as ComputedRef<boolean>,
      prospectCurrentPage: null as CurrentPage | null,
      countryList: [],
      currencyList: [],
      prospectList: null as ComputedRef<Array<any>> | null,
      username: null,
      currentProspectId: null,
      currentProspect: null,
      ProspectPageEnum,
      harmonizedSystemCodes: [],
    };
  },
  computed: {
    tabsStep() {
      return this.prospectCurrentPage?.currentPage;
    },
    displayRegistrations() {
      return useProspect().getDisplayRegistrations();
    },
  },
  methods: {
    async getAllData() {
      this.countryList = await useCore().getCountryList();
      this.currencyList = await useCore().getCurrencyList();
      this.prospectList = await useProspect().getProspectList();
      this.prospectIsLoading = computed(() => useProspect().getIsLoading());
    },
    async navigate(page: ProspectPageEnum) {
      useProspect().hideRegistrations();
      this.prospectCurrentPage.currentPage = page;
    },
    async switchCurrentProspect(prospectId?: number) {
      if (this.prospectList && this.prospectList.prospects?.length > 0) {
        if (prospectId) {
          this.currentProspectId = prospectId;
        } else {
          //Try to get the previous one stored in Local Storage
          const previousProspectId = useLocalStorage(
            LocalStorageConst.CURRENT_PROSPECT_ID,
            null
          ).value;
          if (
            previousProspectId &&
            this.prospectList.prospects.some(
              (p) => p.prospectId === previousProspectId
            )
          ) {
            this.currentProspectId = previousProspectId;
          } else {
            // Get the last prospect in the list
            const currentProspect =
              this.prospectList.prospects[
                this.prospectList.prospects.length - 1
              ];
            this.currentProspectId = currentProspect.prospectId;
          }
        }

        await this.getCurrentProspect();
        this.prospectCurrentPage = await useProspect().getProspectCurrentPage(
          this.currentProspectId
        );

        localStorage.setItem(
          LocalStorageConst.CURRENT_PROSPECT_ID,
          this.currentProspectId!
        );
      }
    },
    async getCurrentProspect() {
      this.currentProspect = await useProspect().getProspect(
        this.currentProspectId
      );

      if (this.currentProspect) {
        this.username = this.currentProspect?.holder?.displayName;
      }
    },
    async selectProspect(prospect: any) {
      await this.switchCurrentProspect(prospect.prospectId);
      await this.navigate(this.prospectCurrentPage.currentPage);
    },
    async updateEntity() {
      await this.getCurrentProspect();
    },
  },
  async created() {
    await this.getAllData();
    this.switchCurrentProspect();
    this.pageIsLoading = false;
  },
};
</script>

<template>
  <div>
    <CoreLoader :visible="pageIsLoading || prospectIsLoading"></CoreLoader>
  </div>
  <div v-if="!pageIsLoading">
    <template v-if="displayRegistrations">
      <RegisterSelectProspect
        :prospect-list="prospectList"
        :select-prospect="selectProspect"
      >
      </RegisterSelectProspect>
    </template>

    <template v-else>
      <!-- Step IndividualLanding (Account Usage) -->
      <template
        v-if="
          prospectCurrentPage?.currentPage ===
          ProspectPageEnum.IndividualLanding
        "
      >
        <div class="step-title">
          <span>{{ $t("register.tabs.individualAccountUsage") }}</span>
        </div>
        <RegisterIndividualAccountUsage
          :prospect-id="currentProspectId"
          :prospect-holder="currentProspect?.holder"
          @navigate="navigate($event)"
        />
      </template>

      <!-- Step IndividualRegistration -->
      <template
        v-if="
          prospectCurrentPage?.currentPage ===
          ProspectPageEnum.IndividualRegistration
        "
      >
        <div class="step-title">
          <span>{{ $t("register.tabs.individualInformation") }}</span>
        </div>
        <RegisterIndividualInformation
          :prospect-id="currentProspectId"
          :prospect-holder="currentProspect?.holder"
          :country-list="countryList"
          @navigate="navigate($event)"
        />
      </template>

      <!-- Step IndividualPreKyc -->
      <template
        v-if="
          prospectCurrentPage?.currentPage === ProspectPageEnum.IndividualPreKyc
        "
      >
        <div class="step-title">
          <span>{{ $t("register.tabs.familyAndJob") }}</span>
        </div>
        <RegisterIndividualFamily
          :prospect-id="currentProspectId"
          :prospect-holder="currentProspect?.holder"
          :country-list="countryList"
          @navigate="navigate($event)"
        />
      </template>

      <!-- Step IndividualContractAndConditions  -->
      <template
        v-if="
          prospectCurrentPage?.currentPage ===
          ProspectPageEnum.IndividualContractAndConditions
        "
      >
        <div class="step-title">
          <span>{{ $t("register.tabs.contract") }}</span>
        </div>
        <RegisterIndividualContract
          :prospect-id="currentProspectId"
          :prospect-holder="currentProspect?.holder"
          @navigate="navigate($event)"
        ></RegisterIndividualContract>
      </template>

      <!-- Step IndividualDocuments  -->
      <template
        v-if="
          prospectCurrentPage?.currentPage ===
          ProspectPageEnum.IndividualDocuments
        "
      >
        <div class="step-title">
          <span>{{ $t("register.tabs.yourDocuments") }}</span>
        </div>
        <RegisterIndividualDocumentsUpload
          :prospect-id="currentProspectId"
          :prospect-holder="currentProspect?.holder"
          @navigate="navigate($event)"
        ></RegisterIndividualDocumentsUpload>
      </template>

      <!-- Step CorporateLanding (Account Usage) -->
      <template
        v-if="
          prospectCurrentPage?.currentPage === ProspectPageEnum.CorporateLanding
        "
      >
        <div class="step-title">
          <span>{{ $t("register.tabs.companyAccountUsage") }}</span>
        </div>
        <RegisterCompanyAccountUsage
          :prospect-id="currentProspectId"
          :prospect-holder="currentProspect?.holder"
          @navigate="navigate($event)"
        />
      </template>

      <!-- Step CorporatePreKyc  -->
      <template
        v-if="
          prospectCurrentPage?.currentPage === ProspectPageEnum.CorporatePreKyc
        "
      >
        <div class="step-title">
          <span>{{ $t("register.tabs.companyInformation") }}</span>
        </div>

        <RegisterCompanyInformation
          :prospect-id="currentProspectId"
          :prospect-holder="currentProspect?.holder"
          :country-list="countryList"
          :currency-list="currencyList"
          @navigate="navigate($event)"
        ></RegisterCompanyInformation>
      </template>

      <!-- Step CorporateStructure  -->
      <template
        v-if="
          prospectCurrentPage?.currentPage ===
          ProspectPageEnum.CorporateStructure
        "
      >
        <div class="mb-3 step-title">
          <span>{{ $t("register.tabs.companyStructure") }}</span>
        </div>
        <RegisterCompanyStructure
          :prospect-id="currentProspectId"
          :prospect="currentProspect"
          :country-list="countryList"
          @navigate="navigate($event)"
          @submit-entity="updateEntity()"
          @revert-entity="updateEntity()"
          @delete-entity="updateEntity()"
        >
        </RegisterCompanyStructure>
      </template>

      <!-- Step CorporateTransaction -->
      <template
        v-if="
          prospectCurrentPage?.currentPage ===
          ProspectPageEnum.CorporateTransaction
        "
      >
        <div class="mb-3 step-title">
          <span>{{ $t("register.tabs.companyTransaction") }}</span>
        </div>
        <RegisterCompanyTransaction
          :prospect-id="currentProspectId"
          :prospect-holder="currentProspect?.holder"
          :prospect-kyc-list="currentProspect?.kycList"
          :currency-list="currencyList"
          @navigate="navigate($event)"
        >
        </RegisterCompanyTransaction>
      </template>

      <!-- Step CorporateCounterparty -->
      <template
        v-if="
          prospectCurrentPage?.currentPage ===
          ProspectPageEnum.CorporateCounterparty
        "
      >
        <div class="mb-3 step-title">
          <span>{{ $t("register.tabs.companyCounterparties") }}</span>
        </div>
        <RegisterCompanyCounterparties
          :country-list="countryList"
          :currency-list="currencyList"
          :prospect-id="currentProspectId"
          :prospect-holder="currentProspect"
          @update-entity="updateEntity()"
          @navigate="navigate($event)"
        >
        </RegisterCompanyCounterparties>
      </template>

      <!-- Step UnderReview  -->
      <template
        v-if="prospectCurrentPage?.currentPage === ProspectPageEnum.UnderReview"
      >
        <div class="step-title">
          <span>{{ $t("register.tabs.underReview") }}</span>
        </div>
        <RegisterUnderReview
          :prospect-id="currentProspectId"
        ></RegisterUnderReview>
      </template>

      <!-- Step Refused  -->
      <template
        v-if="prospectCurrentPage?.currentPage === ProspectPageEnum.Refused"
      >
        <div class="step-title">
          <span>{{ $t("register.tabs.refused") }}</span>
        </div>
        <RegisterRefused :prospect-id="currentProspectId"></RegisterRefused>
      </template>

      <!-- Step Iban (Initial deposit)  -->
      <template
        v-if="prospectCurrentPage?.currentPage === ProspectPageEnum.Iban"
      >
        <div class="step-title">
          <span>{{ $t("register.tabs.initialDeposit") }}</span>
        </div>
        <RegisterInitialDeposit
          :prospect-id="currentProspectId"
          :prospect-holder="currentProspect"
          :username="username"
        ></RegisterInitialDeposit>
      </template>
    </template>
  </div>
</template>

<style scoped lang="scss">
.step-title {
  text-align: center;

  span {
    display: inline-block;
    margin-bottom: 12px;
    font-size: 14px;
    color: #E05C91;
    font-weight: 500;
    letter-spacing: 1.25px;
    text-transform: uppercase;
    line-height: 43px;
    padding: 3px 16px 0;
    border-bottom: solid 3px rgb(196, 99, 144);
  }
}
</style>
